/**
 * FlahaSOIL v2 — Report detail page.
 *
 * Loads a SoilReport with version history and the active snapshot.
 * Clear separation: on-screen preview vs authenticated PDF download.
 */
import {
	Alert,
	Box,
	Button,
	Grid,
	Stack,
	Typography,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type {
	ChartSnapshotsDTO,
	GetReportResponse,
	ReportVersionDTO,
} from "@flaha/shared-types";

import { ReportVersionTimeline } from "../features/reports/components/ReportVersionTimeline";
import { SpecialNote } from "../features/results/components/SpecialNote";
import { ProfessionalReportPreview } from "../features/soil/report";
import { captureChartSnapshots } from "../features/soil/report/captureChartSnapshots";
import { GlassSurface } from "../layouts/components/GlassSurface";
import { usePageHeader } from "../layouts/PageHeaderContext";
import { getApiClient } from "../services/apiClientProvider";
import { useAuth } from "../auth";

export function ReportDetailPage() {
	const { projectId = "", reportId = "" } = useParams<{
		projectId: string;
		reportId: string;
	}>();
	const { status: sessionStatus } = useAuth();
	const [data, setData] = useState<GetReportResponse | null>(null);
	const [activeVersion, setActiveVersion] = useState<ReportVersionDTO | null>(
		null
	);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [downloading, setDownloading] = useState(false);

	usePageHeader({
		title: data?.report.title ?? "Report",
		...(data?.report.reportNumber ? { subtitle: data.report.reportNumber } : {}),
		breadcrumbs: projectId
			? [
					{ label: "Home", to: "/" },
					{ label: "Dashboard", to: "/dashboard" },
					{ label: "Projects", to: "/projects" },
					{ label: "Project", to: `/projects/${projectId}` },
					{ label: "Reports", to: `/projects/${projectId}/reports` },
					{ label: data?.report.title ?? "Report" },
				]
			: [
					{ label: "Home", to: "/" },
					{ label: "Dashboard", to: "/dashboard" },
					{ label: "Reports", to: "/reports" },
					{ label: data?.report.title ?? "Report" },
				],
	});

	const load = useCallback(() => {
		setLoadError(null);
		setActionError(null);
		setData(null);
		setActiveVersion(null);
		getApiClient()
			.getReport(reportId)
			.then((res) => {
				setData(res);
				setActiveVersion(res.currentVersion);
			})
			.catch((err: unknown) =>
				setLoadError(err instanceof Error ? err.message : String(err))
			);
	}, [reportId]);

	useEffect(() => {
		if (sessionStatus !== "authenticated" || !reportId) return;
		load();
	}, [load, reportId, sessionStatus]);

	const onRegenerate = async () => {
		if (busy) return;
		setBusy(true);
		setActionError(null);
		try {
			await getApiClient().regenerateReport(reportId);
			load();
		} catch (err: unknown) {
			setActionError(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	};

	const onDownloadPdf = async () => {
		if (!activeVersion || downloading) return;
		setDownloading(true);
		setActionError(null);
		let objectUrl: string | null = null;
		try {
			let chartSnapshots: ChartSnapshotsDTO = {};
			try {
				chartSnapshots = await captureChartSnapshots();
			} catch (captureErr: unknown) {
				console.warn(
					"Chart snapshot capture failed; generating PDF without embedded charts.",
					captureErr
				);
			}
			const blob = await getApiClient().getReportVersionPdfBlob(
				reportId,
				activeVersion.versionNumber,
				chartSnapshots
			);
			objectUrl = URL.createObjectURL(blob);
			const anchor = document.createElement("a");
			anchor.href = objectUrl;
			anchor.download = `${data?.report.reportNumber ?? "report"}-v${activeVersion.versionNumber}.pdf`;
			document.body.appendChild(anchor);
			anchor.click();
			anchor.remove();
			setActionError(null);
		} catch (err: unknown) {
			const raw = err instanceof Error ? err.message : String(err);
			// Surface a clearer message than the browser's opaque NetworkError.
			const friendly =
				/networkerror|failed to fetch|network request failed|load failed/i.test(
					raw
				)
					? "Download failed due to a network interruption. Please try again — if it keeps failing, charts may be skipped automatically on retry."
					: raw;
			setActionError(friendly);
		} finally {
			if (objectUrl) URL.revokeObjectURL(objectUrl);
			setDownloading(false);
		}
	};

	const onSelectVersion = async (versionNumber: number) => {
		setActionError(null);
		try {
			const { version } = await getApiClient().getReportVersion(
				reportId,
				versionNumber
			);
			setActiveVersion(version);
		} catch (err: unknown) {
			setActionError(err instanceof Error ? err.message : String(err));
		}
	};

	if (loadError)
		return (
			<Alert
				severity="error"
				action={
					<Button color="inherit" size="small" onClick={load}>
						Retry
					</Button>
				}
			>
				Failed: {loadError}
			</Alert>
		);
	if (!data || !activeVersion)
		return <Typography color="text.secondary">Loading report…</Typography>;

	return (
		<Grid container spacing={2}>
			<Grid item xs={12} md={9}>
				{actionError && (
					<Alert
						severity="error"
						onClose={() => setActionError(null)}
						sx={{ mb: 2 }}
					>
						Failed: {actionError}
					</Alert>
				)}

				<GlassSurface
					intensity="strong"
					depth="raised"
					sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }}
				>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						justifyContent="space-between"
						alignItems={{ xs: "stretch", sm: "center" }}
						spacing={2}
					>
						<Box>
							<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
								<VisibilityIcon sx={{ color: "workspace.blue", fontSize: 20 }} aria-hidden />
								<Typography variant="overline" sx={{ color: "workspace.blue", letterSpacing: "0.1em", fontWeight: 700 }}>
									On-screen preview
								</Typography>
							</Stack>
							<Typography variant="h6">
								Version {activeVersion.versionNumber}
								{data.report.reportNumber ? ` · ${data.report.reportNumber}` : ""}
							</Typography>
							<Typography variant="body2" sx={{ color: "workspace.textMuted", mt: 0.5, maxWidth: 520 }}>
								What you see below is the interactive preview. PDF download
								packages this version for sharing and archive — it does not
								create a new version by itself.
							</Typography>
						</Box>
						<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
							<Button
								variant="contained"
								startIcon={<PictureAsPdfIcon />}
								onClick={onDownloadPdf}
								disabled={downloading}
							>
								{downloading ? "Preparing PDF…" : "Download PDF"}
							</Button>
							<Button
								variant="outlined"
								startIcon={<RefreshIcon />}
								onClick={onRegenerate}
								disabled={busy}
							>
								{busy ? "Regenerating…" : "Regenerate version"}
							</Button>
						</Stack>
					</Stack>
				</GlassSurface>

				<SpecialNote kind="methodology" title="Preview vs PDF" dense>
					<strong>On-screen preview</strong> is for review in the browser.
					<strong> Download PDF</strong> is the authenticated, version-locked
					file for clients and records. Regenerate appends a new immutable
					version when inputs or engines change.
				</SpecialNote>

				<Box sx={{ mt: 2 }}>
					<ProfessionalReportPreview
						snapshot={activeVersion.snapshot}
						versionNumber={activeVersion.versionNumber}
						generatedAt={activeVersion.generatedAt}
					/>
				</Box>
			</Grid>
			<Grid item xs={12} md={3}>
				<ReportVersionTimeline
					versions={data.versions}
					activeVersionNumber={activeVersion.versionNumber}
					onSelect={onSelectVersion}
				/>
			</Grid>
		</Grid>
	);
}
